/**
 * The nullable-cap control.
 *
 * ── The rule this component exists to enforce ──
 * A budget cap has THREE states, not two:
 *   • `null`  → unlimited. Usage is still metered; there is no ceiling.
 *   • `0`     → hard block. Every call 402s.
 *   • `n > 0` → a ceiling of n.
 *
 * A blank field means `null`. A typed `0` means `0`. The classic bug is
 * `Number(e.target.value) || 0`, or `parseFloat('') → NaN → 0`, which
 * silently converts "unlimited" into "blocked" — i.e. turns a working
 * tenant into a dead one on a save the admin thought was a no-op. So:
 * parsing is explicit, blank short-circuits to `null` BEFORE any numeric
 * coercion, and an unparseable string is rejected rather than defaulted.
 *
 * The two states are also visually distinct at rest (a blue "No ceiling"
 * chip vs a red "Blocked" chip), because two states that both look like
 * an empty box are two states an admin will confuse.
 *
 * The chip used to prefix an ∞ glyph, which made ONE state read two ways
 * across adjacent screens — "No ceiling" in the editor's legend and on the
 * dashboard, "∞ No ceiling" in the chip beside the field. Correct in each
 * place, unlearnable across them (L4). The colour already carries the
 * distinction the glyph was decorating.
 */
/*
 * ── WHY THIS LIVES IN `@papan/core-ui` ──────────────────────────────────
 *
 * It was in `features/orgs/ui/budget/` until 2026-08-18, and `features/billing`
 * — which owns the PRICE BOOK, where the same ceilings are set on a preset —
 * cannot import it there: `@papan/orgs` depends on `@papan/billing`, never the
 * reverse. So the price book hand-rolled plain `<input>`s for included budget,
 * storage and concurrency, and the two screens drifted into different controls
 * with different vocabulary for the same field.
 *
 * That is exactly why `Modal`, `ActionDisclosure` and `DirtyBar` are here:
 * both feature packages compose the console and neither may import the other,
 * so a component owned by either gets re-implemented by the other. This is the
 * same rule applied to the field that most needs it — a control whose whole
 * job is making three states distinguishable is worth nothing if one screen
 * has it and the other does not.
 *
 * (Its i18n strings were already in the `core-ui` namespace, which was the
 * clue that this was its home all along.)
 */
import { useEffect, useRef, useState } from 'react'
import { useDraftState } from './draft-store'
import { useTranslation } from '@papan/i18n'
import type { BudgetCap } from '@papan/types'

/**
 * Parse user input into a cap. Returns `undefined` for "not a valid
 * number" so the caller can refuse the commit instead of inventing a
 * value. Blank is meaningful (unlimited) and never falls through to the
 * numeric path.
 */
export function parseCapInput(raw: string, opts: { allowNegative?: boolean } = {}): BudgetCap | undefined {
  const s = raw.trim()
  if (s === '') return null // unlimited — NOT zero
  const n = Number(s)
  if (!Number.isFinite(n)) return undefined
  if (n < 0 && !opts.allowNegative) return undefined
  return n
}

/** Dollars (what an admin types) → microdollars (what the wire carries). */
export const toMicro = (dollars: number) => Math.round(dollars * 1_000_000)
/** Microdollars → dollars, for seeding the input. */
export const toDollars = (micro: number) => micro / 1_000_000

function capToText(cap: BudgetCap, scale: 'money' | 'int'): string {
  if (cap === null) return ''
  return scale === 'money' ? String(toDollars(cap)) : String(cap)
}

export interface CapInputProps {
  /** Current value. Money caps are microdollars; `int` caps are raw ints. */
  value: BudgetCap
  /** Fired on blur / Enter, only when the text parses. */
  onCommit: (next: BudgetCap) => void
  /** `money` accepts dollars and commits microdollars; `int` is 1:1. */
  scale?: 'money' | 'int'
  /** Member adjustments may be negative (the documented zero-out pattern). */
  allowNegative?: boolean
  /**
   * What `null` means here. Defaults to "Unlimited". Override for knobs
   * where blank is NOT unlimited — e.g. `agentConcurrencyCap: null` falls
   * back to the global default of 3, and a team cap of `null` contributes
   * nothing.
   */
  nullLabel?: string
  /**
   * Whether a typed `0` is a hard block. False for additive contributions
   * (team caps, member adjustments) where 0 just means "adds nothing".
   */
  zeroIsBlock?: boolean
  /**
   * Suppress the null-state chip. For dense tables where the blank input's
   * placeholder already announces the state and the chip's width is what
   * forces the table into horizontal scroll. The Blocked and invalid chips
   * always render — those two must never be less than loud.
   */
  hideNullChip?: boolean
  disabled?: boolean
  /** Currency code shown as a prefix inside the input. Omit for NO prefix —
   *  dense tables state the currency once in the column header instead of
   *  spending 36px repeating it in every row. */
  currencyCode?: string
  /**
   * Screen-reader name for the field ("Marketing team — free contribution").
   * Without it the aria-label falls back to the placeholder, which announces
   * the field's NULL state ("Unlimited"), not what the field is.
   */
  ariaLabel?: string
  className?: string
  /** Tailwind width class for the input itself. Default w-28; dense tables
   *  pass w-24 (enough for the "Unlimited" placeholder with no prefix). */
  inputWidthClass?: string
  /**
   * Render the typed amount converted into the tenant's DISPLAY currency, as a
   * muted line beneath the field.
   *
   * ── Why this exists ──────────────────────────────────────────────────
   *
   * Caps are typed and stored in canonical USD — the write endpoints take USD
   * microdollars and `currencyCode` says so on the field. But every figure the
   * admin just read on the Spending dashboard is in the tenant's display
   * currency ("≈RM 330 spent of ≈RM 1,100"). So "cap them at roughly what I
   * just saw" silently required knowing the credit rate, and there was nothing
   * on either screen relating the two numbers.
   *
   * This does NOT change the value, the units, or the wire format. It is a
   * read-out, which is why it takes a formatter rather than a rate: converting
   * here would duplicate the `?? 1` coalesce CLAUDE.md bans, and `useMoney`
   * has already resolved the chain.
   *
   * Omitted when the tenant displays in USD — `useMoney().approximate` is
   * false there and the line would restate the field.
   */
  displayHint?: (micro: number) => string
  /**
   * #897 — names this field's typed-but-uncommitted text (the page only sees a
   * value on blur / Enter), so it survives a phone/desktop layout swap. Absent =
   * plain local state.
   */
  draftKey?: string
}

export function CapInput({
  value,
  onCommit,
  scale = 'money',
  allowNegative = false,
  nullLabel,
  zeroIsBlock = true,
  hideNullChip = false,
  disabled,
  currencyCode,
  ariaLabel,
  className = '',
  inputWidthClass = 'w-28',
  displayHint,
  draftKey,
}: CapInputProps) {
  const { t } = useTranslation('core-ui')
  // The typed text is a DRAFT over the stored value: absent, the field shows
  // `value` (live); typed, it shows the draft — kept across a layout swap when
  // `draftKey` names it (#897).
  const [text, setText, { clear: clearText }] = useDraftState<string>(draftKey ?? null, () => capToText(value, scale))
  const [invalid, setInvalid] = useState(false)

  // Re-seed when the server value changes underneath us (refetch, or another
  // field's save returning fresh state) — only on a real CHANGE: a draft the
  // swap restored must survive the field's first mount (and StrictMode's).
  const seenRef = useRef({ value, scale })
  useEffect(() => {
    if (seenRef.current.value === value && seenRef.current.scale === scale) return
    seenRef.current = { value, scale }
    clearText()
    setInvalid(false)
  }, [value, scale, clearText])

  /** What `raw` would commit; `undefined` = unparseable. */
  const toCommit = (raw: string): BudgetCap | undefined => {
    const parsed = parseCapInput(raw, { allowNegative })
    if (parsed === undefined) return undefined
    return parsed === null ? null : scale === 'money' ? toMicro(parsed) : Math.round(parsed)
  }
  // The text the field shows for `value`, and what THAT would commit. The field
  // shows `value` rounded in places (money above ~2^52 micro), so a change is
  // measured against the untouched text, never against `value` itself: else
  // focusing and leaving an untouched field would write the rounded amount.
  const seed = capToText(value, scale)
  const seedCommit = toCommit(seed)

  const commit = () => {
    const next = toCommit(text)
    if (next === undefined) {
      setInvalid(true)
      return
    }
    setInvalid(false)
    if (text !== seed && next !== seedCommit && next !== value) onCommit(next)
    // Handed to the page (or no change): the field shows what the page holds.
    clearText()
  }

  const unlimited = text.trim() === ''
  const isZero = !unlimited && Number(text) === 0 && Number.isFinite(Number(text))

  /**
   * The equivalent in the tenant's display currency, for a REAL positive
   * amount only.
   *
   * Skipped for blank (unlimited) and 0 (blocked) because neither is a
   * quantity — "≈RM 0.00" beside a red "Blocked" chip states nothing and
   * competes with the chip that carries the meaning. Skipped for `int` caps,
   * which are not money at all. Parsed from the LIVE text rather than `value`,
   * so it tracks what is being typed rather than what was last committed —
   * which is the whole point on a field the admin is comparing to a figure
   * they read on another screen.
   */
  const hint = (() => {
    if (!displayHint || scale !== 'money' || unlimited || isZero) return null
    const parsed = parseCapInput(text, { allowNegative })
    if (parsed === undefined || parsed === null) return null
    return displayHint(toMicro(parsed))
  })()

  return (
    <div className={className}>
    <div className="flex items-center gap-1.5">
      <div className="relative flex items-center">
        {scale === 'money' && currencyCode && (
          <span className="absolute left-2 text-[10px] themed-text-muted pointer-events-none">{currencyCode}</span>
        )}
        <input
          type="text"
          inputMode="decimal"
          value={text}
          disabled={disabled}
          onChange={e => { setText(e.target.value); setInvalid(false) }}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.currentTarget.blur() }
            if (e.key === 'Escape') { clearText(); setInvalid(false) }
          }}
          placeholder={nullLabel ?? t('settings.budget.unlimited')}
          aria-label={ariaLabel ?? nullLabel ?? t('settings.budget.unlimited')}
          aria-invalid={invalid || undefined}
          className={`themed-input border rounded ${scale === 'money' && currencyCode ? 'pl-9' : 'pl-2'} pr-2 py-1 text-xs ${inputWidthClass} outline-none focus:border-blue-500 disabled:opacity-50 ${
            invalid ? 'border-red-500' : ''
          }`}
        />
      </div>
      {/* The chips are the whole point: blank and 0 must not look alike.
          (hideNullChip only drops the blank-state chip — the placeholder
          still names the state — never the Blocked one.) */}
      {unlimited && !hideNullChip && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 whitespace-nowrap">
          {nullLabel ?? t('settings.budget.unlimited')}
        </span>
      )}
      {isZero && zeroIsBlock && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 whitespace-nowrap" title={t('settings.budget.blockedHint')}>
          {t('settings.budget.blocked')}
        </span>
      )}
      {invalid && (
        <span className="text-[10px] text-red-400 whitespace-nowrap">{t('settings.budget.invalidAmount')}</span>
      )}
    </div>
    {/* The same amount in the currency the dashboards print, so an admin who
        came from "≈RM 1,100 ceiling" can see that "200" is the right number to
        type. Muted and below the field: it is a read-out, never an input. */}
    {hint && (
      <div className="text-[10px] themed-text-muted mt-0.5 tabular-nums">{hint}</div>
    )}
    </div>
  )
}

/**
 * A non-nullable byte quota, typed in GB. Storage quota has no "unlimited"
 * state on the wire (it is a plain `storageQuotaBytes` number), so this is
 * deliberately NOT a `CapInput` — blank is invalid here, not meaningful.
 */
export function GbInput({
  bytes,
  onCommit,
  disabled,
  ariaLabel,
  draftKey,
}: {
  bytes: number
  onCommit: (nextBytes: number) => void
  disabled?: boolean
  /** Screen-reader name; defaults to the generic "Storage quota". */
  ariaLabel?: string
  /** #897 — names the typed text so it survives a phone/desktop layout swap, as `CapInput`. */
  draftKey?: string
}) {
  const { t } = useTranslation('core-ui')
  const GBv = 1024 ** 3
  // A draft over the stored size, as `CapInput`'s text.
  const [text, setText, { clear: clearText }] = useDraftState<string>(draftKey ?? null, () => String(+(bytes / GBv).toFixed(3)))
  const [invalid, setInvalid] = useState(false)

  // Re-seed on a real CHANGE of the stored size only (a restored draft survives mount).
  const seenRef = useRef(bytes)
  useEffect(() => {
    if (seenRef.current === bytes) return
    seenRef.current = bytes
    clearText()
    setInvalid(false)
  }, [bytes, clearText])

  /** What `raw` would commit, in bytes; `undefined` = invalid (blank is invalid here). */
  const toCommit = (raw: string): number | undefined => {
    const s = raw.trim()
    const n = Number(s)
    if (s === '' || !Number.isFinite(n) || n < 0) return undefined
    return Math.round(n * GBv)
  }
  // The field shows `bytes` rounded to 3 decimals (500 000 000 → "0.466"), so a
  // change is measured against the untouched text, never against `bytes`: else
  // focusing and leaving an untouched field would write the rounded size.
  const seed = String(+(bytes / GBv).toFixed(3))
  const seedCommit = toCommit(seed)

  const commit = () => {
    const next = toCommit(text)
    if (next === undefined) { setInvalid(true); return }
    setInvalid(false)
    if (text !== seed && next !== seedCommit && next !== bytes) onCommit(next)
    clearText()
  }

  return (
    <div className="flex items-center gap-1.5">
      <input
        type="text"
        inputMode="decimal"
        value={text}
        disabled={disabled}
        onChange={e => { setText(e.target.value); setInvalid(false) }}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') e.currentTarget.blur()
          // Back out of an edit — the one way out of an invalid entry (blank is
          // invalid here).
          if (e.key === 'Escape') { clearText(); setInvalid(false) }
        }}
        aria-label={ariaLabel ?? t('settings.budget.storageQuota')}
        aria-invalid={invalid || undefined}
        className={`themed-input border rounded px-2 py-1 text-xs w-24 outline-none focus:border-blue-500 disabled:opacity-50 ${invalid ? 'border-red-500' : ''}`}
      />
      <span className="text-[10px] themed-text-muted">GB</span>
    </div>
  )
}
